import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../../../context/LanguageContext';
import '../style/about-section.css';
import { initializeAboutSection } from '../script/about-section';
import { initializeTitleAnimation } from '../script/title-animation';
import { getDocs, query, where } from 'firebase/firestore';
import { getClientCollection } from '../../../../../services/FirebaseService';
import sobreNos1 from '../../../../../assets/logo/about_us/sobre_nos_1.webp';
import sobreNos2 from '../../../../../assets/logo/about_us/sobre_nos_2.webp';
import aboutShape1 from '../../../../../assets/images/shapes/about-one-shape-1.png';
import aboutShape2 from '../../../../../assets/images/shapes/about-one-shape-2.png';

const AboutSection: React.FC = () => {
    const { t, language } = useLanguage();
    const containerRef = useRef<HTMLElement>(null);
    const videoLinkRef = useRef<HTMLAnchorElement>(null);
    const [videoUrl, setVideoUrl] = useState<string | null>(null);
    const odometerRef = useRef<HTMLHeadingElement>(null);

    const translation = t as any;
    const aboutTranslations = translation.ABOUT || t.ABOUT;

    // Calculate years of experience dynamically (founding year: 2007)
    const foundingYear = 2007;
    const currentYear = new Date().getFullYear();
    const yearsOfExperience = currentYear - foundingYear;

    // Replace hardcoded years in translation text with dynamic value
    const getDynamicAboutText = (text: string | undefined): string => {
        if (!text) return '';
        // Replace any number followed by "anos" or "years" with dynamic value
        return text.replace(/\d+\s*(anos|years)/gi, `${yearsOfExperience} $1`);
    };

    // Load about video from Firestore
    useEffect(() => {
        const loadAboutVideo = async () => {
            try {
                let videosSnap;
                let allVideos: any[] = [];
                
                try {
                    const aboutQuery = query(
                        getClientCollection('videos'),
                        where('type', '==', 'about')
                    );
                    videosSnap = await getDocs(aboutQuery);
                    
                    if (videosSnap.docs.length > 0) {
                        allVideos = videosSnap.docs.map(doc => ({
                            id: doc.id,
                            ...doc.data()
                        }));
                    }
                } catch (whereError) {
                    try {
                        const allVideosQuery = query(getClientCollection('videos'));
                        videosSnap = await getDocs(allVideosQuery);
                        
                        allVideos = videosSnap.docs.map(doc => {
                            const data = doc.data();
                            return {
                                id: doc.id,
                                ...data
                            };
                        });
                    } catch (allError) {
                        return;
                    }
                }
                
                let aboutVideo = allVideos.find((video: any) => {
                    return video.type === 'about';
                });
                
                if (aboutVideo) {
                    const url = (aboutVideo as any).url || (aboutVideo as any).videoUrl || (aboutVideo as any).downloadURL;
                    if (url) {
                        setVideoUrl(url);
                        if (videoLinkRef.current) {
                            videoLinkRef.current.setAttribute('href', url);
                            videoLinkRef.current.setAttribute('data-video-url', url);
                            videoLinkRef.current.setAttribute('data-video-source', 'firebase');
                        }
                    }
                }
            } catch (_) {
            }
        };

        loadAboutVideo();
    }, []);

    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        // Update odometer data-count attribute with calculated years
        if (odometerRef.current) {
            odometerRef.current.setAttribute('data-count', yearsOfExperience.toString());
        }

        // Initialize about section features (odometer, video popup)
        initializeAboutSection(containerRef.current);
    }, [videoUrl, yearsOfExperience]);

    // Initialize title animation only once on first page load
    useEffect(() => {
        if (!containerRef.current) {
            return;
        }

        const hasInitialized = sessionStorage.getItem('about-titles-initialized');
        
        const timer = setTimeout(() => {
            if (!hasInitialized) {
            initializeTitleAnimation();
                sessionStorage.setItem('about-titles-initialized', 'true');
            } else {
                // On language change, just make titles visible without animation
                const titles = document.querySelectorAll('.about-one .title-animation');
                titles.forEach((title: Element) => {
                    const el = title as HTMLElement;
                    el.style.opacity = '1';
                });
            }
            
            // Refresh ScrollTrigger to recalculate positions
            if (typeof window !== 'undefined' && (window as any).ScrollTrigger) {
                (window as any).ScrollTrigger.refresh();
            }
        }, 200);

        return () => {
            clearTimeout(timer);
        };
    }, [language]); // Re-run when language changes

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>, fallbackSrc: string) => {
        const target = e.target as HTMLImageElement;
        if (target.src !== fallbackSrc) {
            target.onerror = null;
            target.src = fallbackSrc;
        }
    };

    return (
        <section className="about-one" ref={containerRef}>
            <div className="about-one__shape-1 float-bob-x">
                <img loading="lazy" src={aboutShape1} alt="" />
            </div>
            <div className="about-one__shape-2 float-bob-y" style={{ left: '0px' }}>
                <img loading="lazy" src={aboutShape2} alt="" style={{ transform: 'scaleX(-1)' }} />
            </div>
            <div className="container">
                <div className="row">
                    <div className="col-xl-6">
                        <div className="about-one__left wow slideInLeft" data-wow-delay="100ms" data-wow-duration="2500ms">
                            <div className="about-one__img-box">
                                <div className="about-one__img">
                                    <img loading="lazy" 
                                        src={sobreNos1} 
                                        alt="" 
                                        style={{ width: '455px', height: '470px' }}
                                        onError={(e) => handleImageError(e, '/assets/logo/about_us/sobre nós oliveira de azeméis 455 x 470.webp')}
                                    />
                                    <div className="about-one__video-link">
                                        <a 
                                            href={videoUrl || "https://www.youtube.com/watch?v=CR9Y3Do-aX8"} 
                                            className="video-popup"
                                            ref={videoLinkRef}
                                        >
                                            <div className="about-one__video-icon">
                                                <span className="fa fa-play"></span>
                                                <i className="ripple"></i>
                                            </div>
                                        </a>
                                    </div>
                                </div>
                                <div className="about-one__img-two">
                                    <img loading="lazy"
                                        src={sobreNos2} 
                                        alt=""
                                        style={{ width: '350px', height: '310px' }}
                                        onError={(e) => handleImageError(e, '/assets/logo/about_us/sobre nós oliveira de azeméis 350 x 310.webp')}
                                    />
                                </div>
                                <div className="about-one__experience-box">
                                    <div className="about-one__experience-count">
                                        <h3 className="odometer" ref={odometerRef} data-count={yearsOfExperience}>00</h3>
                                        <span>+</span>
                                    </div>
                                    <p className="about-one__experience-count-text">
                                        {aboutTranslations?.yearsExperience || 'Anos De Experiência'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="col-xl-6">
                        <div className="about-one__right">
                            <div className="section-title text-left sec-title-animation animation-style2">
                                <div className="section-title__tagline-box">
                                    <div className="section-title__tagline-border">
                                        <div className="section-title__shape-1">
                                            <i className="section-title__circle"></i>
                                        </div>
                                    </div>
                                    <h6 className="section-title__tagline">
                                        {aboutTranslations?.tagline || 'SOBRE NÓS'}
                                    </h6>
                                    <div className="section-title__tagline-border">
                                        <div className="section-title__shape-2">
                                            <i className="section-title__circle"></i>
                                        </div>
                                    </div>
                                </div>
                                <h3 
                                    className="section-title__title title-animation"
                                    key={`about-title-${language}`}
                                    dangerouslySetInnerHTML={{ __html: aboutTranslations?.title || 'A nossa especialização<br>está na reparação e<br>manutenção<br>automóvel.' }}
                                ></h3>
                            </div>
                            <p 
                                className="about-one__text"
                                dangerouslySetInnerHTML={{ __html: getDynamicAboutText(aboutTranslations?.text) || `Com mais de ${yearsOfExperience} anos de experiência, o Roady Oliveira de Azeméis garante um serviço técnico de excelência em todas as áreas da manutenção automóvel.<br>Apostamos em diagnóstico preciso, intervenção rigorosa e acompanhamento profissional — tudo para assegurar o melhor desempenho e segurança do seu veículo.` }}
                            ></p>
                            <div className="about-one__point-box">
                                <ul className="about-one__point">
                                    <li>
                                        <div className="about-one__point-icon">
                                            <span className="icon-affordable"></span>
                                        </div>
                                        <div className="about-one__point-content">
                                            <h4>{aboutTranslations?.fastService || 'Serviço técnico especializado'}</h4>
                                            <p>{aboutTranslations?.fastServiceText || 'Intervenções realizadas por técnicos certificados e equipamentos de diagnóstico avançado.'}</p>
                                        </div>
                                    </li>
                                </ul>
                                <ul className="about-one__point">
                                    <li>
                                        <div className="about-one__point-icon">
                                            <span className="icon-24-hours"></span>
                                        </div>
                                        <div className="about-one__point-content">
                                            <h4>{aboutTranslations?.onlineSupport || 'Suporte completo e proximidade'}</h4>
                                            <p>{aboutTranslations?.onlineSupportText || 'Aconselhamento personalizado e acompanhamento contínuo dos nossos clientes.'}</p>
                                        </div>
                                    </li>
                                </ul>
                            </div>
                            <div className="about-one__point-two-and-review-box">
                                <div>
                                    <ul className="about-one__point-two">
                                        <li>
                                            <div className="icon">
                                                <span className="fas fa-check"></span>
                                            </div>
                                            <div className="text">
                                                <p>
                                                    {aboutTranslations?.qualityControlSystem || 'Sistema interno de controlo de qualidade'}
                                                </p>
                                            </div>
                                        </li>
                                        <li>
                                            <div className="icon">
                                                <span className="fas fa-check"></span>
                                            </div>
                                            <div className="text">
                                                <p>
                                                    {aboutTranslations?.affordableCost || 'Equipa técnica certificada'}
                                                </p>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                                <div>
                                    <ul className="about-one__point-two">
                                        <li>
                                            <div className="icon">
                                                <span className="fas fa-check"></span>
                                            </div>
                                            <div className="text">
                                                <p>
                                                    {aboutTranslations?.bestTireBrands || 'As melhores marcas internacionais de pneus'}
                                                </p>
                                            </div>
                                        </li>
                                        <li>
                                            <div className="icon">
                                                <span className="fas fa-check"></span>
                                            </div>
                                            <div className="text">
                                                <p>
                                                    {aboutTranslations?.expressOilChange || 'Mudança de óleo express'}
                                                </p>
                                            </div>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="about-one__btn-and-author-box">
                                <div className="about-one__btn-box">
                                    <Link to="/orcamento" className="thm-btn about-read-more-btn">
                                        {translation.BUTTONS?.requestQuote || 'Pedir Orçamento'}
                                        <span><i className="icon-next"></i></span>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AboutSection;

